import React, { useState, useRef, useEffect } from "react";
import { useHotel } from "../../contexts/HotelContext";

const TenantSwitcher: React.FC = () => {
  const { user, selectedTenantId, setSelectedTenant, currentTenant } =
    useHotel();
  const [isOpen, setIsOpen] = useState(false);
  const dropdownRef = useRef<HTMLDivElement>(null);

  // Close dropdown when clicking outside
  useEffect(() => {
    const handleClickOutside = (event: MouseEvent) => {
      if (
        dropdownRef.current &&
        !dropdownRef.current.contains(event.target as Node)
      ) {
        setIsOpen(false);
      }
    };

    if (isOpen) {
      document.addEventListener("mousedown", handleClickOutside);
    }

    return () => {
      document.removeEventListener("mousedown", handleClickOutside);
    };
  }, [isOpen]);

  // Hide for single-tenant users or users with no tenants
  if (!user?.tenants || user.tenants.length <= 1) {
    return null;
  }

  const handleSelectTenant = (tenantId: string) => {
    setSelectedTenant(tenantId);
    setIsOpen(false);
    // Force page reload to refresh all data with new tenant context
    window.location.reload();
  };

  return (
    <div className="relative" ref={dropdownRef}>
      <button
        onClick={() => setIsOpen(!isOpen)}
        className="flex items-center gap-2 px-3 py-2 text-sm font-medium text-gray-700 bg-white border border-gray-300 rounded-md hover:bg-gray-50 focus:outline-none focus:ring-2 focus:ring-offset-2 focus:ring-primary-500 transition-colors"
        aria-haspopup="true"
        aria-expanded={isOpen}
      >
        <span className="truncate max-w-[200px]">
          {currentTenant?.name || "Wybierz organizację"}
        </span>
        <svg
          className={`w-4 h-4 transition-transform ${
            isOpen ? "transform rotate-180" : ""
          }`}
          fill="none"
          stroke="currentColor"
          viewBox="0 0 24 24"
        >
          <path
            strokeLinecap="round"
            strokeLinejoin="round"
            strokeWidth={2}
            d="M19 9l-7 7-7-7"
          />
        </svg>
      </button>

      {isOpen && (
        <div className="absolute right-0 mt-2 w-64 bg-white rounded-md shadow-lg border border-gray-200 z-50">
          <div className="py-1" role="menu" aria-orientation="vertical">
            {user.tenants.map((tenant) => {
              const isSelected = tenant.id === selectedTenantId;
              return (
                <button
                  key={tenant.id}
                  onClick={() => handleSelectTenant(tenant.id)}
                  className={`w-full text-left px-4 py-2 text-sm transition-colors ${
                    isSelected
                      ? "bg-primary-50 text-primary-800 font-medium"
                      : "text-gray-700 hover:bg-gray-50"
                  }`}
                  role="menuitem"
                >
                  <div className="flex items-center justify-between">
                    <span className="truncate">{tenant.name}</span>
                    {isSelected && (
                      <svg
                        className="w-4 h-4 text-primary-700"
                        fill="currentColor"
                        viewBox="0 0 20 20"
                      >
                        <path
                          fillRule="evenodd"
                          d="M16.707 5.293a1 1 0 010 1.414l-8 8a1 1 0 01-1.414 0l-4-4a1 1 0 011.414-1.414L8 12.586l7.293-7.293a1 1 0 011.414 0z"
                          clipRule="evenodd"
                        />
                      </svg>
                    )}
                  </div>
                </button>
              );
            })}
          </div>
        </div>
      )}
    </div>
  );
};

export default TenantSwitcher;
